import { Injectable } from '@angular/core';
import { ActivityStatus, GroupAppFilter } from '../_models/models';

/**
 * Service to handle active/inactive item management for dropdowns, displays, and filterin
 */
@Injectable({
  providedIn: 'root'
})
export class ItemStatusService {

  private readonly ACTIVE_STATUS = ActivityStatus.ACTIVE_LOWER;
  private readonly INACTIVE_STATUS = ActivityStatus.INACTIVE_LOWER;
  private readonly INACTIVE_SUFFIX = ' (inactive)';



  filterDropdownItemsFromStatusGroupsLists<T extends { id: number | string }>(
    activeItems: T[],
    inactiveItems: T[],
    labelGetter: (item: T) => string,
    currentSelectedItems: T[] | T | null = null
  ): Array<T> {

    if (!currentSelectedItems) {
      return activeItems.map(item => ({ ...item, displayLabel: labelGetter(item) }));
    }
    const selectedArray = Array.isArray(currentSelectedItems) ? currentSelectedItems : [currentSelectedItems];
    const selectedIds = new Set(selectedArray.map(item => item.id));

    const selectedActiveItems = activeItems.filter(item => selectedIds.has(item.id));
    const nonSelectedActiveItems = activeItems.filter(item => !selectedIds.has(item.id));

    const selectedInactiveItems = inactiveItems.filter(item => selectedIds.has(item.id));

    return [...selectedActiveItems.map(item => ({ ...item, displayLabel: labelGetter(item) })),
      ...selectedInactiveItems.map(item => ({ ...item, displayLabel: `${labelGetter(item)}${this.INACTIVE_SUFFIX}` })),
      ...nonSelectedActiveItems.map(item => ({ ...item, displayLabel: labelGetter(item) }))];
  }

  filterDropdownFromAllItems<T extends { id: number | string }>(
    activeItems: T[],
    allItems: T[],
    labelGetter: (item: T) => string,
    currentSelectedItems: T[] | T | null = null
  ): Array<T> {
    const inactiveItems = allItems.filter(item => !activeItems.some(a => a.id === item.id));
    return this.filterDropdownItemsFromStatusGroupsLists(activeItems, inactiveItems, labelGetter, currentSelectedItems);
  }

  private createStatusGroup<T>(
    items: T[],
    groupId: number,
    groupName: string,
    groupDisplayName: string,
    filterMapper: (item: T) => { id: string | number; displayName: string; value: string | number }
  ): GroupAppFilter {
    return {
      groupId,
      groupName,
      groupDisplayName,
      possibleValues: items.map(filterMapper)
    };
  }


  createGroupedActiveInactiveFilters<T extends { status: string }>(
    allItems: T[],
    filterMapper: (item: T) => { id: string | number; displayName: string; value: string | number }
  ): GroupAppFilter[] {
    const activeItems = allItems.filter(item => item.status === this.ACTIVE_STATUS);
    const inactiveItems = allItems.filter(item => item.status === this.INACTIVE_STATUS);

    const groups: GroupAppFilter[] = [
      this.createStatusGroup(activeItems, 1, 'active', 'Active', filterMapper)
    ];

    if (inactiveItems.length > 0) {
      groups.push(this.createStatusGroup(inactiveItems, 2, 'inactive', 'Inactive', filterMapper));
    }

    return groups;
  }

}
